import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import { useSnackbar } from "notistack";
import { styled } from "@mui/material/styles";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
// MUI core components
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Rating,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Chip,
  Button,
  Pagination,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
  Tooltip,
  Breadcrumbs,
  Link,
  TextField,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Modal,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Radio,
  FormControl,
  InputLabel,
  Fade,
} from "@mui/material";

// MUI icons
import {
  Add,
  Remove,
  Share,
  ArrowBackIos,
  ArrowForwardIos,
  Close,
  Favorite,
  FavoriteBorder,
  GridView,
  List as ListIcon,
  LocalGasStation,
  DirectionsCar,
  AddShoppingCart,
  ChatBubbleOutline,
} from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

// Local components
import Notes from "./header";
import BasicSelectFilter from "./filter";
import FooterManual from "./footerManual";
import InquiryModal from "./inquiryModal";

// Redux actions
import {
  addToWishlist,
  fetchCart,
  fetchWishlist,
  fetchCarsByModelName,
} from "./store";


// Responsive Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
  overflow: "hidden",
  marginBottom: "16px",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    width: `calc(100% + ${theme.spacing(2)})`,
    "& .MuiTable-root": { minWidth: "650px" },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  [theme.breakpoints.down("sm")]: {
    "& > .MuiTableCell-root": { padding: theme.spacing(1) },
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#f5f5f5",
  fontWeight: "bold",
  position: "sticky",
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    fontSize: "0.75rem",
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiCardMedia-root": { objectFit: "contain" },
    "& .MuiCardContent-root": { padding: "20px 8px" },
    "& .MuiButton-root": {
      fontSize: "0.7rem",
      padding: "4px 8px",
      minWidth: "unset",
    },
    "& .MuiTypography-h6": { fontSize: "0.9rem" },
    "& .MuiTypography-body2": { fontSize: "0.75rem" },
    "& .MuiChip-root": {
      height: "20px",
      "& .MuiChip-label": { fontSize: "0.65rem" },
    },
  },
}));

const StickyFilterContainer = styled("div")(({ theme, visible }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: "white",
  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.3s ease-in-out",
  transform: visible ? "translateY(0)" : "translateY(-100%)",
  [theme.breakpoints.down("sm")]: {
    "& > *": { padding: theme.spacing(1) },
  },
}));

const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
  // animation: "spin 1.5s linear infinite",
  // "@keyframes spin": {
  //   "0%": { transform: "rotate(0deg)" },
  //   "100%": { transform: "rotate(360deg)" },
  // },
}));

// Utility function to remove duplicate brand name from model name
const cleanModelName = (brandName, modelName) => {
  if (!brandName || !modelName) return modelName || '';
  
  // Convert both to lowercase for case-insensitive comparison
  const brandLower = brandName.toLowerCase().trim();
  const modelLower = modelName.toLowerCase().trim();
  
  // Check if model name starts with brand name
  if (modelLower.startsWith(brandLower)) {
    // Remove the brand name from the beginning
    return modelName.substring(brandName.length).trim();
  }
  
  // Return original model name if no duplicate found
  return modelName;
};

// Responsive Car Card Component
const CarListingCardGrid = ({
  car,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
  onInquiryClick
}) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const isFavorite = wishlistItems.some((item) => item.modelId === car.carModelId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState(null);


  const handleConfirmMove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${car.carModelId}&SelectType=MOVE_TO_CART`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
          body: "",
        }
      );

      if (response.ok) {
        enqueueSnackbar("Item moved to cart successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
        dispatch(fetchCart());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to move item to cart", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error moving item to cart", { variant: "error" });
      console.error("Error moving to cart:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${car.carModelId}`,
        {
          method: "DELETE",
          headers: {
            accept: "application/json",
            "Accept-Language": "en",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        enqueueSnackbar("Item removed from wishlist successfully", {
          variant: "success",
        });
        dispatch(fetchWishlist());
      } else {
        const data = await response.json();
        enqueueSnackbar(data.message || "Failed to remove item from wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error removing item from wishlist", {
        variant: "error",
      });
      console.error("Error removing from wishlist:", error);
    } finally {
      setOpenDialog(false);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }
      if (isFavorite) {
        setDialogType("remove");
        setOpenDialog(true);
        return;
      }
      const result = await dispatch(addToWishlist(car.carModelId));
      if (result.success) {
        enqueueSnackbar("Added to wishlist successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar(result.message || "Failed to add to wishlist", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to wishlist", { variant: "error" });
      console.error("Error adding to wishlist:", error);
    }
  };


  return (
    <StyledCard
      // onClick={() => onCarClick(car.carModelId)} // Removed to match globalSearch.js for card body click
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          transform: "translateY(-4px)",
        },
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      {/* Image Section with Hover Effect */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: isMobile ? "180px" : "250px",
        }}
        onClick={() => onCarClick(car.carModelId)} // Added click handler here
      >



        <CardMedia
          component="img"
          // height="100%"
          image={imageUrl}
          alt={`${car.carBrandName} ${car.carModelName}`}
          // 👇 Add the onError handler here 👇
          onError={(e) => {
              // Prevent an infinite loop if the fallback also fails
              e.target.onerror = null; 
              
          // Set the source to the local image in the public folder
          e.target.src = '/carousel/carslider.png'; 
      }}
          sx={{
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
            objectFit: isMobile ? "contain" : "cover",
            width: "100%",
            height: "100%",
          }}
          onClick={() => onCarClick(car.carModelId)} // Added click handler here
        />

        {/* Favorite Button */}
        <IconButton
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.9)",
            padding: isMobile ? "4px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.2s ease",
          }}
          onClick={handleFavoriteClick}
        >
          {isFavorite ? (
            <Favorite
              sx={{
                color: "red",
                fontSize: isMobile ? "1rem" : "1.25rem",
                animation: isFavorite ? "pulse 0.5s ease" : "none",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.2)" },
                  "100%": { transform: "scale(1)" },
                },
              }}
            />
          ) : (
            <FavoriteBorder
              sx={{
                fontSize: isMobile ? "1rem" : "1.25rem",
                color: theme.palette.grey[600],
              }}
            />
          )}
        </IconButton>

      </Box>

      {/* Badges Overlay - Moved from CardContent and slightly adjusted margin */}
        <Box
          sx={{
            marginTop: "-15px",
            marginLeft: "15px",
            bottom: 8,
            left: 8,
            display: "flex",
            gap: 0.5,
            flexWrap: "wrap",
            zIndex: 111,
          }}
        >
          {car.ourPick && (
            <Chip
              label="Our Pick"
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "26px",
                color: "#dd006e",
                backgroundColor: "#ffbddf",
                fontWeight: "bold",
                border: "1px solid #FF69B4",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}
          {car.carGrade && (
            <Chip
              label={`Grade ${car.carGrade || "A"}`}
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "26px",
                color: "#009966",
                backgroundColor: "#9fffe7",
                fontWeight: "bold",
                border: "1px solid #00FFAA",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}
          {car.specialDeal && (
            <Chip
              label="Special Offer"
              size="small"
              sx={{
                fontSize: isMobile ? "0.55rem" : "0.65rem",
                height: isMobile ? "16px" : "26px",
                backgroundColor: "#d09fff",
                color: "#6c2aab",
                fontWeight: "bold",
                border: "1px solid #6c2aab",
                "&:hover": { backgroundColor: "white" },
              }}
            />
          )}

        </Box>

      {/* Content Section */}
      <CardContent
        sx={{
          flexGrow: 1,
          p: isMobile ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => onCarClick(car.carModelId)} // Added click handler here
      >
        {/* Title and Basic Info - UPDATED to match globalSearch.js */}
        <Box >
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              fontWeight: 700,
              // lineHeight: 1.2, // Removed
              mb: 0.5, // Removed
              color: theme.palette.text.primary,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
          </Typography>

          {/* Removed car category name Typography to match globalSearch.js */}
          {/* <Typography
            variant="body2"
            sx={{
              fontSize: isMobile ? "0.75rem" : "0.8125rem",
              color: theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <span>{car.carCategoriesName}</span>
          </Typography> */}

          {/* Rating Section - REMOVED to match globalSearch.js */}
          {/* <Rating
            value={5}
            readOnly
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiRating-iconFilled": {
                color: theme.palette.warning.main,
              },
            }}
          /> */}
        </Box>

        {/* Specifications Grid - UPDATED to remove Color and keep Ref#, Year, Engine, Fuel */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 1,

            "& > div": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            },
          }}
        >
          {/* Ref # */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Ref #:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carRefNumber || "N/A"}
            </Typography>
          </Box>

          {/* Year */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Year:
            </Typography>
            {car.year && <span>{car.year}</span>}
          </Box>

          {/* Engine */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Engine:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "0.75rem" : "0.8125rem",
              }}
            >
              {car.carEngineCC || "N/A"}
            </Typography>
          </Box>

          {/* Fuel and Transmission Chips - REMOVED Transmission part */}
          <Box
            sx={{
              display: "flex",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Fuel:
            </Typography>

            {car.carFuel && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? "0.65rem" : "0.75rem",
                  color: theme.palette.text.primary,
                }}
              >
                {car.carFuel}
              </Typography>
            )}

            {/* Removed carTransmission display */}
          </Box>

          {/* Color - REMOVED to match globalSearch.js */}
          {/* <Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: isMobile ? "0.65rem" : "1rem",
              }}
            >
              Color:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? "0.75rem" : "0.8125rem",
                }}
              >
                {car.carColor || "N/A"}
              </Typography>
            </Box>
          </Box> */}
        </Box>

        {/* Chassis - REMOVED to match globalSearch.js */}
        {/* <Box sx={{ display: "flex", gap: "8px", alignItems: "baseline" }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "1rem",
            }}
          >
            Chassis:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontFamily: "monospace",
              // wordBreak: "break-all",
              fontSize: isMobile ? "0.75rem" : "0.8125rem",
            }}
          >
            {car.carChassisNumber || "N/A"}
          </Typography>
        </Box> */}

        {/* Price Section */}
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              fontSize: isMobile ? "0.65rem" : "20px",
            }}
          >
            Price:
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? "1rem" : "20px",
              fontWeight: 700,
              color: theme.palette.error.main,
            }}
          >
            {car.carPrice
              ? `$${car.carPrice.toLocaleString()}`
              : "Price on request"}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Buttons - UPDATED to match globalSearch.js (only Inquiry) */}
      <Box
        sx={{
          p: isMobile ? 1 : 1.5,
          pt: 0,
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* ADD TO CART Button - REMOVED to match globalSearch.js */}
        {/* <Button
          variant="contained"
          size="medium"
          sx={{
            bgcolor: "red",
            color: "white",
            border: "1px solid red",
            borderRadius: "50px",
            fontSize: "0.75rem",
            fontWeight:"600",
            padding: "18px 18px",
            height: "38px",
            width: "100%", // Full width
            transition: "all 0.3s ease",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "red",
              transform: "translateX(-100%)",
              transition: "transform 0.3s ease",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
              "&::before": {
                transform: "translateX(0)",
              },
            },
          }}
          disabled={cartItems.some(
            (item) => item.modelId === car.carModelId
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (isFavorite) {
              setDialogType("move");
              setSelectedModelId(car.carModelId);
              setOpenDialog(true);
            } else {
              onAddToCart(car.carModelId);
            }
          }}
        >
          {isFavorite
            ? "MOVE TO CART"
            : cartItems.some(
              (item) => item.modelId === car.carModelId
            )
              ? "ADDED"
              : "ADD TO CART"}
        </Button> */}

        <Button
          variant="contained"
          size="medium"
          sx={{
            bgcolor: "red",
            color: "white",
            border: "1px solid red",
            borderRadius: "50px", // Smaller border radius
            fontSize: "0.75rem", // Smaller font size
            fontWeight:"600",
            padding: "18px 18px", // Reduced padding
            minWidth: "150px", // Reduced minimum width
            height: "38px", // Explicitly set smaller height
            width: "100%", // Full width
            transition: "all 0.3s ease",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "red",
              transform: "translateX(-100%)",
              transition: "transform 0.3s ease",
              zIndex: -1,
            },
            "&:hover": {
              color: "white",
              boxShadow: "0 3px 6px rgba(255,0,0,0.3)", // Smaller shadow
              "&::before": {
                transform: "translateX(0)",
              },
            },
          }}
          onClick={(e) => onInquiryClick(e, car.carModelId)}
        >
          INQUIRY
        </Button>
      </Box>


      {/* Updated Confirmation Modal (Kept as is for functionality) */}
      <Modal
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedModelId(null);
          setDialogType("");
        }}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          overflow: 'hidden',
        }}>
          <Box sx={{
            px: 3,
            py: 2,
          }}>
            <Typography
              id="dialog-title"
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
              }}
            >
              {dialogType === "move"
                ? "Confirm Move to Cart"
                : "Remove from Wishlist"}
            </Typography>
          </Box>

          <Box sx={{ px: 3, py: 3 }}>
            <Typography id="dialog-description">
              {dialogType === "move"
                ? "Are you sure you want to move this item to your cart?"
                : "Are you sure you want to remove this item from your wishlist?"}
            </Typography>
          </Box>

          <Box sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
            <Button
              onClick={() => {
                setOpenDialog(false);
                setSelectedModelId(null);
                setDialogType("");
              }}
              sx={{
                border: "1px solid rgb(54 54 54)",
                color: "#282828",
                padding: "0px 18px"
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={
                dialogType === "move" ? handleConfirmMove : handleConfirmRemove
              }
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                },
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </StyledCard>
  );
};

// Responsive Table Component
const CarListingTable = ({
  cars,
  onCarClick,
  onAddToWishlist,
  onAddToCart,
  imageUrl,
  onInquiryClick
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const { enqueueSnackbar } = useSnackbar();
  const wishlistItems = useSelector((state) => state.wishlist.items);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const visibleColumns = isMobile ? 10 : isTablet ? 10 : 11;
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [selectedModelId, setSelectedModelId] = useState(null);


  const handleFavoriteClick = async (e, carModelId, isFavorite) => {
    e.stopPropagation();
    setSelectedModelId(carModelId);

    if (isFavorite) {
      // Show removal confirmation dialog
      setDialogType("remove");
      setOpenDialog(true);
    } else {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          navigate("/login");
          return;
        }
        const result = await dispatch(addToWishlist(carModelId));
        if (result.success) {
          enqueueSnackbar("Added to wishlist successfully", {
            variant: "success",
          });
        } else if (result.alreadyInWishlist) {
          enqueueSnackbar("This item is already in your wishlist", {
            variant: "info",
          });
        } else {
          enqueueSnackbar(result.message || "Failed to add to wishlist", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Error adding to wishlist", { variant: "error" });
        console.error("Error adding to wishlist:", error);
      }
    }
  };

  const handleConfirmAction = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      if (dialogType === "remove") {
        // DELETE API call for removing from wishlist
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${selectedModelId}`,
          {
            method: "DELETE",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          enqueueSnackbar("Removed from wishlist successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to remove from wishlist", {
            variant: "error",
          });
        }
      } else if (dialogType === "move") {
        // POST API call for moving to cart
        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${selectedModelId}&SelectType=MOVE_TO_CART`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Accept-Language": "en",
              Authorization: `Bearer ${token}`,
            },
            body: "",
          }
        );
        if (response.ok) {
          enqueueSnackbar("Item moved to cart successfully", {
            variant: "success",
          });
          dispatch(fetchWishlist());
          dispatch(fetchCart());
        } else {
          const data = await response.json();
          enqueueSnackbar(data.message || "Failed to move item to cart", {
            variant: "error",
          });
        }
      }
    } catch (error) {
      enqueueSnackbar("Error processing request", { variant: "error" });
      console.error("Error:", error);
    } finally {
      setOpenDialog(false);
      setSelectedModelId(null);
      setDialogType("");
    }
  };

  return (
   <StyledTableContainer component={Paper}>
      <Table stickyHeader sx={{ minWidth: isMobile ? "650px" : "800px" }}>
        <TableHead>
          <TableRow>
            <StyledTableHeaderCell align="center">
              Drive This Beauty
            </StyledTableHeaderCell>
            {visibleColumns > 1 && (
              <StyledTableHeaderCell align="center">
                Referance Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 2 && (
              <StyledTableHeaderCell align="center">
                Chassis Number
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 3 && (
              <StyledTableHeaderCell align="center">Registration Year</StyledTableHeaderCell>
            )}
            {visibleColumns > 4 && (
              <StyledTableHeaderCell align="center">
                Engine CC / Motor kW
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 5 && (
              <StyledTableHeaderCell align="center">Fuel Type</StyledTableHeaderCell>
            )}
            {/* {visibleColumns > 6 && (
              <StyledTableHeaderCell align="center">A/C</StyledTableHeaderCell>
            )} */}
            {visibleColumns > 7 && (
              <StyledTableHeaderCell align="center">
                Body Color
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 8 && (
              <StyledTableHeaderCell align="center">
                Drive Type
              </StyledTableHeaderCell>
            )}
            {visibleColumns > 9 && (
              <StyledTableHeaderCell align="center">
                Price
              </StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell align="center">
              Actions
            </StyledTableHeaderCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {cars.map((car) => {
            const isFavorite = wishlistItems.some(
              (item) => item.modelId === car.carModelId
            );
            const carImageUrl =
              imageUrl[car.carModelId] || "carousel/carslider.png";

            return (
              <StyledTableRow
                key={car.carModelId}
                onClick={() => onCarClick(car.carModelId)}
              >
                <TableCell
                  sx={{
                    px: { xs: 1, sm: 2 },
                    // maxWidth: isMobile ? "120px" : "unset",
                    minWidth: isMobile ? "180px" : "250px", 
                    maxWidth: isMobile ? "200px" : "300px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 1 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: isMobile ? 60 : 80,
                        height: isMobile ? 45 : 60,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={carImageUrl}
                        alt={`${car.carBrandName} ${car.carModelName}`}
                        // 👇 Add the onError handler here 👇
                        onError={(e) => {
                            // Prevent an infinite loop if the fallback also fails
                            e.target.onerror = null; 
                            
                            // Set the source to the local image in the public folder
                            e.target.src = '/carousel/carslider.png'; 
                        }}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 1,
                          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                        }}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) =>
                          handleFavoriteClick(e, car.carModelId, isFavorite)
                        }
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          padding: isMobile ? "2px" : "3px",
                          backgroundColor: "rgba(255,255,255,0.8)",
                        }}
                      >
                        {isFavorite ? (
                          <Favorite sx={{ fontSize: isMobile ? 10 : 14 }} />
                        ) : (
                          <FavoriteBorder
                            sx={{ fontSize: isMobile ? 10 : 14 }}
                          />
                        )}
                      </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          fontSize: isMobile ? "0.75rem" : "0.875rem",
                          // textOverflow: "ellipsis",
                          // overflow: "hidden",
                          // whiteSpace: "nowrap",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          lineHeight: 1.3,
                        }}
                      >
                        {car.carBrandName} {cleanModelName(car.carBrandName, car.carModelName)} {car.year}
                      </Typography>
                      {/* <Rating
                        value={5}
                        readOnly
                        size="small"
                        sx={{ fontSize: isMobile ? "0.6rem" : "0.75rem", mt: 0.5 }}
                      /> */}
                    </Box>
                  </Box>
                </TableCell>

                {/* {visibleColumns > 1 && (
                  <TableCell sx={{ padding: isMobile ? "4px" : "8px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {car.ourPick && (
                        <Chip
                          label="Our Pick"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "24px",
                            color: "#dd006e", // Neon pink
                            backgroundColor: "#ffbddf",
                            fontWeight: "bold",

                            border: "1px solid #FF69B4",
                            "&:hover": { backgroundColor: "white" },
                          }}
                        />
                      )}
                      <Chip
                        label={`Grade ${car.carGrade || "A"}`}
                        size="small"
                        sx={{
                          fontSize: isMobile ? "0.55rem" : "0.65rem",
                          height: isMobile ? "16px" : "24px",
                          color: "#009966", // Neon mint

                          backgroundColor: "#9fffe7",
                          fontWeight: "bold",
                          border: "1px solid #00FFAA",
                          "&:hover": { backgroundColor: "white" },
                        }}
                      />
                      {car.specialDeal && (
                        <Chip
                          label="Special Offer"
                          size="small"
                          sx={{
                            fontSize: isMobile ? "0.55rem" : "0.65rem",
                            height: isMobile ? "16px" : "24px",
                            backgroundColor: "#d09fff",

                            color: "#6c2aab", // Periwinkle
                            fontWeight: "bold",
                            border: "1px solid #70f8ff",
                            "&:hover": { backgroundColor: "white", color: "green" },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                )} */}

                 {visibleColumns > 1 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carRefNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 2 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carChassisNumber || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 3 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.year || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 4 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/engine.svg"
                        alt="Engine"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {/* {car.carEngineCC || "N/A"} */}
                      {car.carEngineCC ? `${car.carEngineCC}` : "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 5 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <img
                        src="/BrandIcons/MDI - gas-station.svg"
                        alt="Fuel"
                        sx={{ fontSize: isMobile ? "0.9rem" : "1rem" }}
                      />
                      {car.carFuel || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {/* {visibleColumns > 6 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                    >
                      {car.airConditioning || "N/A"}
                    </Typography>
                  </TableCell>
                )} */}

                {visibleColumns > 7 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? 12 : 16,
                          height: isMobile ? 12 : 16,
                          borderRadius: "50%",
                          backgroundColor:
                            car.carColor?.toLowerCase() || "#ccc",
                          border: "1px solid #ddd",
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
                      >
                        {car.carColor || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                )}

                {visibleColumns > 8 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontSize: isMobile ? "0.7rem" : "0.875rem",
                        fontFamily: "monospace",
                      }}
                    >
                      {car.carDrive || "N/A"}
                    </Typography>
                  </TableCell>
                )}

                {visibleColumns > 9 && (
                  <TableCell
                    align="center"
                    sx={{ padding: isMobile ? "4px" : "8px" }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.75rem" : "0.95rem",
                        color: "error.main",
                      }}
                    >
                      {car.carPrice
                        ? `$${car.carPrice.toLocaleString()}`
                        : "On Request"}
                    </Typography>
                  </TableCell>
                )}

                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.8, // Slightly reduced gap between buttons
                      position: "relative",
                      padding: "6px 0", // Reduced padding
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        bgcolor: "red",
                        color: "white",
                        border: "1px solid red",
                        borderRadius: "16px",
                        fontSize: "0.75rem",
                        padding: "3px 12px",
                        minWidth: "80px",
                        height: "28px",
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          background: "red",
                          transform: "translateX(-100%)",
                          transition: "transform 0.3s ease",
                          zIndex: -1,
                        },
                        "&:hover": {
                          color: "white",
                          boxShadow: "0 3px 6px rgba(255,0,0,0.3)",
                          "&::before": {
                            transform: "translateX(0)",
                          },
                        },
                      }}
                      onClick={(e) => onInquiryClick(e, car.carModelId)}
                    >
                      INQUIRY
                    </Button>
                    {/* <Button
                      variant="contained"
                      size="small" // Already using small size
                      sx={{
                        bgcolor: "red",
                        color: "white",
                        borderRadius: "16px", // Smaller border radius
                        fontSize: "0.75rem", // Smaller font size
                        padding: "3px 12px", // Reduced padding
                        minWidth: "80px", // Reduced minimum width
                        height: "28px", // Explicitly set smaller height
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:disabled": {
                          bgcolor: "#cccccc",
                          cursor: "not-allowed",
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: "30px",
                          height: "100%",
                          top: 0,
                          left: "-50px",
                          backgroundColor: "rgba(255,255,255,0.5)",
                          transform: "skewX(-25deg)",
                          transition: "all 0.4s ease",
                        },
                        "&:hover": {
                          bgcolor: "#c00",
                          boxShadow: "0 4px 10px rgba(255,0,0,0.3)", // Smaller shadow
                          "&::after": {
                            left: "120%",
                          },
                        },
                      }}
                      disabled={cartItems.some(
                        (item) => item.modelId === car.carModelId
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFavorite) {
                          setDialogType("move");
                          setSelectedModelId(car.carModelId);
                          setOpenDialog(true);
                        } else {
                          onAddToCart(car.carModelId);
                        }
                      }}
                    >
                      {isFavorite
                        ? "MOVE TO CART"
                        : cartItems.some(
                          (item) => item.modelId === car.carModelId
                        )
                          ? "ADDED"
                          : "ADD TO CART"}
                    </Button> */}
                  </Box>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Confirmation Modal */}
      <Modal
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedModelId(null);
          setDialogType("");
        }}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="dialog-title" variant="h6" component="h2">
            {dialogType === "move"
              ? "Confirm Move to Cart"
              : "Remove from Wishlist"}
          </Typography>
          <Typography id="dialog-description" sx={{ mt: 2 }}>
            {dialogType === "move"
              ? "Are you sure you want to move this item to your cart?"
              : "Are you sure you want to remove this item from your wishlist?"}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              sx={{
                border: "1px solid rgb(54 54 54)",
                color: "#282828",
                padding: "0px 18px"
              }}
              onClick={() => {
                setOpenDialog(false);
                setSelectedModelId(null);
                setDialogType("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#b71c1c", // halka dark hover ke liye
                },
              }}
            >
              Confirm
            </Button>

          </Box>
        </Box>
      </Modal>
    </StyledTableContainer>
  );
};

// Main Component
const ModelNameByGlobalSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [viewMode, setViewMode] = useState(isMobile ? "grid" : "list");
  const [showFilter, setShowFilter] = useState(false);
  const [imageUrl, setimageUrl] = useState({});
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [showStickyFilter, setShowStickyFilter] = useState(false);
  const [noContentMessage, setNoContentMessage] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const location = useLocation();
  const { modelName: selectedModelName } = useParams();
//   const KNOWN_BRANDS = [
//   "MERCEDES BENZ", "BMW", "AUDI", "SEAT", "TOYOTA", "ALFAROMEO",
//   "ASTON MARTIN", "CHEVROLET", "BENTLEY", "CITROEN", "FIAT", "HYUNDAI",
// ];

// const extractBrandAndModel = (modelName) => {
//   if (!modelName) return { brand: null };
//   const upper = modelName.toUpperCase();
//   const foundBrand = KNOWN_BRANDS.find(b => upper.startsWith(b));
//   return { brand: foundBrand || null };
// };

// const { brand: extractedBrand } = extractBrandAndModel(selectedModelName);

// const filterStateForBar = {
//   selectedBrand: extractedBrand ? { label: extractedBrand } : null,
//   selectedModels: selectedModelName ? [{ carModelName: selectedModelName }] : [],
//   selectedCategory: null,
//   selectedYears: [],
// };
const KNOWN_BRANDS = [
  "MERCEDES BENZ", "BMW", "AUDI", "SEAT", "TOYOTA", "ALFAROMEO",
  "ASTON MARTIN", "CHEVROLET", "BENTLEY", "CITROEN", "FIAT", "HYUNDAI",
];

  const extractBrandAndModel = (modelName) => {
    if (!modelName) return { brand: null };
    const upper = modelName.toUpperCase();
    const foundBrand = KNOWN_BRANDS.find(b => upper.startsWith(b));
    return { brand: foundBrand || null };
  };

  const getBrandForFilter = () => {
    if (location.state?.brandName) {
      return location.state.brandName.toUpperCase();
    }
    const { brand } = extractBrandAndModel(selectedModelName);
    if (brand) return brand;
    return null;
  };

  const [detectedBrand, setDetectedBrand] = useState(getBrandForFilter);

  const filterStateForBar = {
    selectedBrand: detectedBrand ? { label: detectedBrand } : null,
    selectedModels: selectedModelName ? [{ carModelName: selectedModelName }] : [],
    selectedCategory: null,
    selectedYears: [],
  };

  const modelNameCarsState = useSelector((state) => state.modelNameCars);
  const { itemsByModel, loading, error, totalItems, totalPages, currentModel } = modelNameCarsState;
  const cars = itemsByModel[selectedModelName] || [];
  const isLoading = loading && currentModel === selectedModelName;
  const currentError = currentModel === selectedModelName ? error : null;
  const cartItems = useSelector((state) => state.cart.items);

  // States for Inquiry Modal
  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [inquiryProduct, setInquiryProduct] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  useEffect(() => {
     if (isMobile) {
       setViewMode("grid");
     } else {
       setViewMode("list");
     }
   }, [isMobile]);


  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedModelName,
  ]);

  useEffect(() => {
  if (!detectedBrand && cars && cars.length > 0) {
    const firstCar = cars[0];
    if (firstCar?.carBrandName) {
      setDetectedBrand(firstCar.carBrandName.toUpperCase());
    }
  }
}, [cars, detectedBrand]);

  const paginatedCars = Array.isArray(cars) ? cars : [];

  // Scroll handler for sticky filter
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      setShowStickyFilter(
        prevScrollPos > currentScrollPos && currentScrollPos > 100
      );
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [prevScrollPos]);

  // Data fetching and image loading effects
  useEffect(() => {
    const fetchData = async () => {
      if (selectedModelName) {
        const result = await dispatch(
          fetchCarsByModelName(selectedModelName, currentPage - 1, itemsPerPage)
        );

        if (result?.noContent) {
          setNoContentMessage(`No cars found for model "${selectedModelName}"`);
        }
      }
    };

    fetchData();
  }, [dispatch, selectedModelName, currentPage, itemsPerPage]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1); // Reset to first page
  };

  const fetchProductData = async (carModelId) => {
    try {
      const response = await axios.get(
        `https://car.amar.co.jp/api/dashboard/product/get/Model/details/${carModelId}`
      );
      return response.data?.data || null;
    } catch (err) {
      console.error('Error fetching product data:', err);
      return null;
    }
  };

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    });
  };

  const generateValidImageUrls = async (type, modelId, maxCount = 15) => {
    const baseUrl = `https://car.amar.co.jp/api/image/car-${type}-images-`;

    const urls = await Promise.allSettled(
      Array.from({ length: maxCount }, (_, i) =>
        checkImageExists(`${baseUrl}${i + 1}/get-image/${modelId}`)
      )
    );

    return urls
      .filter(result => result.status === 'fulfilled' && result.value)
      .map(result => result.value);
  };

  const handleInquiryClick = async (e, carModelId) => {
    e.stopPropagation();
    const productData = await fetchProductData(carModelId);
    if (productData) {
      setInquiryProduct(productData);
      const imageUrls = await generateValidImageUrls("exterior", carModelId);
      setModalImage(imageUrls[0] || 'carousel/carslider.png');
      setOpenInquiryModal(true);
    } else {
      enqueueSnackbar('Failed to load car details for inquiry.', { variant: 'error' });
    }
  };

  const handleInquiryClose = () => {
    setOpenInquiryModal(false);
    setInquiryProduct(null);
    setModalImage(null);
  };


  // Add this function to fetch car images
  const fetchCarImage = async (carModelId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`
      );

      if (response.ok) {
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error("Error fetching car image:", error);
      return null;
    }
  };

  // Load images when car data changes
  useEffect(() => {
    if (cars && cars.length > 0) {
      cars.forEach(async (car) => {
        if (!imageUrl[car.carModelId]) {
          const fetchedImageUrl = await fetchCarImage(car.carModelId);
          if (fetchedImageUrl) {
            setimageUrl((prev) => ({
              ...prev,
              [car.carModelId]: fetchedImageUrl,
            }));
          }
        }
      });
    }
  }, [cars, imageUrl]);

  // View mode handler
  const handleViewMode = (event, newViewMode) => {
    if (newViewMode !== null) setViewMode(newViewMode);
  };

  // Car click handler
  const handleCarClick = (modelId) => {
    navigate(`/product/${modelId}`);
  };

  // Add to cart handler
  const handleAddToCart = async (modelId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      // Check if item is already in cart
      const isInCart = cartItems.some((item) => item.modelId === modelId);
      if (isInCart) {
        enqueueSnackbar("Item is already in your cart", { variant: "warning" });
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/master/car-cart-and-whishlist/manage/item?modelId=${modelId}&SelectType=ADD_IN_CART`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            modelId: modelId,
            type: "ADD_IN_CART",
          }),
        }
      );

      const data = await response.json();
      if (data.code === 200) {
        enqueueSnackbar("Added to cart successfully", { variant: "success" });
        dispatch(fetchCart()); // Update Redux cart state
      } else {
        enqueueSnackbar(data.message || "Failed to add to cart", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error adding to cart", { variant: "error" });
      console.error("Error adding to cart:", error);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Searching for {selectedModelName}...
        </Typography>
      </Box>
    );
  }

  if (currentError) {
    return (
      <Container>
        <Typography color="error">
          Error loading {selectedModelName}: {currentError}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ padding: 0, margin: 0 }}>
      {/* <Box sx={{ marginBottom: "0%" }}><Notes /></Box> */}
      <Box sx={{ marginBottom: "0%" }}>
        <Notes initialFilterState={filterStateForBar} />
      </Box>

      <StickyFilterContainer visible={showStickyFilter}>
        <Container maxWidth="lg">
          <Box
            sx={{
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          ></Box>
        </Container>
      </StickyFilterContainer>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 3,
            mb: 4,
            borderRadius: "10px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 2 : 0,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "1.25rem" : "1.5rem",
                }}
              >
                {selectedModelName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {totalItems || 0} {totalItems === 1 ? 'result' : 'results'} found
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewMode}
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiToggleButton-root": {
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  color: "black",
                  "&.Mui-selected": {
                    backgroundColor: "red",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#cc0000",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list">
                <ListIcon fontSize={isMobile ? "small" : "medium"} />
              </ToggleButton>
              <ToggleButton value="grid">
                <GridView fontSize={isMobile ? "small" : "medium"} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        {cars.length === 0 ? (
          <Typography
            variant="h6"
            sx={{ textAlign: "center", margin: "40px 0" }}
          >
            {noContentMessage || "No cars found"}
          </Typography>
        ) : viewMode === "list" ? (
          <CarListingTable
            cars={paginatedCars}
            onCarClick={handleCarClick}
            onAddToWishlist={() => { }}
            onAddToCart={handleAddToCart}
            imageUrl={imageUrl}
            cartItems={cartItems}
            onInquiryClick={handleInquiryClick}
          />
        ) : (
          <Grid container spacing={isMobile ? 1 : 3}>
            {paginatedCars.map((car) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={car.carModelId}>
                <CarListingCardGrid
                  car={car}
                  onCarClick={handleCarClick}
                  onAddToWishlist={() => { }}
                  onAddToCart={handleAddToCart}
                  imageUrl={
                    imageUrl[car.carModelId] || "carousel/carslider.png"
                  }
                  cartItems={cartItems}
                  onInquiryClick={handleInquiryClick}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {cars.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              mb: 4,
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "black",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Results per page: {itemsPerPage}
              </Typography>
            </Box>

            <Pagination
              count={totalPages || 0}
              page={currentPage}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  borderColor: "black",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                    borderColor: "red",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "red !important",
                  color: "white",
                  borderColor: "red",
                  "&:hover": {
                    backgroundColor: "#b30000 !important",
                    borderColor: "#800000",
                  },
                },
              }}
            />

            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              variant="outlined"
              displayEmpty
              size="small"
              sx={{
                width: 80,
                color: "black",
                fontWeight: 500,
                backgroundColor: "#fff",
                borderColor: "black",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "black",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red",
                },
                "& .MuiSvgIcon-root": {
                  color: "red",
                },
              }}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
        )}
      </Container>
      <InquiryModal
        open={openInquiryModal}
        onClose={handleInquiryClose}
        product={inquiryProduct}
        imageUrl1={modalImage}
      />
      <FooterManual />
    </Container>
  );
};

export default ModelNameByGlobalSearch;